import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import CategoryMultiSelectWrapper from 'Deer/components/CategoryMultiSelect/CategoryMultiSelectWrapper';
import { RecurrenceSelect } from 'Deer/components/common/RecurrenceSelect';
import { generateDefaultRecurrenceRule } from 'Deer/components/common/RecurrenceSelect/helper';
import { addDays, addWeeks, addYears, differenceInDays, format, parseISO, startOfDay, startOfToday } from 'date-fns';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { setSurveyCreationPresetLoadKey, useSurveyCreationData } from 'reducers/surveyCreationSlice';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';
import { SectionWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/SectionWrapper';
import { checkEnableSurveyPresetsAndRecurrence } from 'utils/helpers/featureFlags';

import { PARTICIPANTS_SELECTION_MODULES } from 'constants/ProjectConstants';

import { DatePicker } from 'uiToolkitV1/DatePicker';
import { InputV2 } from 'uiToolkitV1/InputV2';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useUpdateSurveyForm, useUpdateSurveySettings } from '../dataHook';
import { EngagementFrequencyState, FREQUENCY_LABELS, FREQUENCY_TYPES } from '../types';
import { usePresetInitializationInfo } from '../usePresetInitializationInfo';

type SurveyNameSectionProps = {
  surveyId: string;
  defaultSurveyName: string;
  surveyStartDate: string | null;
  surveyEndDate: string | null;
  isReadOnly: boolean;
  selectedFilters: {
    LOCATION: (string | null)[] | null;
    COMPANY_ID: { companyName: string; id: number } | null;
  };
  totalEmployeeCount: number | null;
  revieweeSelectionType: string | null;
};

export const SurveyNameSection = ({
  surveyId,
  defaultSurveyName,
  surveyStartDate,
  surveyEndDate,
  isReadOnly,
  selectedFilters,
  totalEmployeeCount,
  revieweeSelectionType,
}: SurveyNameSectionProps) => {
  const [surveyName, setSurveyName] = useState(defaultSurveyName || '');
  const [frequency, setFrequency] = useState<EngagementFrequencyState>(() => {
    if (surveyStartDate && surveyEndDate) {
      const startDate = parseISO(surveyStartDate);
      const endDate = parseISO(surveyEndDate);
      return { type: FREQUENCY_TYPES.ONE_TIME, date: startDate, openFor: differenceInDays(endDate, startDate) };
    }
    return { type: FREQUENCY_TYPES.ONE_TIME, date: addWeeks(new Date(), 1), openFor: 7 };
  });
  const [categoryMultiSelectData, setCategoryMultiSelectData] = useState({
    feedbackFormId: surveyId,
    selectedFilters,
    totalEmployeeCount,
    revieweeSelectionType,
  });

  const { mutate: updateSurveyForm } = useUpdateSurveyForm(surveyId);
  const { mutate: updateSurveySettings } = useUpdateSurveySettings(surveyId);

  const { isPresetInitialization, presetInfo } = usePresetInitializationInfo();
  const { isHandledPresetNameLoad, isHandledFrequencyLoad } = useSurveyCreationData();

  const dispatch = useDispatch();

  // syncing state with prop changes
  useEffect(() => {
    if (surveyStartDate && surveyEndDate) {
      const startDate = parseISO(surveyStartDate);
      const endDate = parseISO(surveyEndDate);
      const openForDays = differenceInDays(endDate, startDate);
      setFrequency((prev) => ({ ...prev, date: startDate, openFor: openForDays }));
    }
  }, [surveyStartDate, surveyEndDate]);

  const dateConstraints = useMemo(
    () => ({
      min: startOfToday(),
      max: startOfDay(addYears(new Date(), 1)),
    }),
    []
  );

  // Debounced function to update survey form
  const debouncedUpdateSurveyForm = useMemo(
    () =>
      debounce((name: string) => {
        updateSurveyForm({ surveyId, updatedFormFields: { name } });
      }, 500),
    [surveyId, updateSurveyForm]
  );

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedUpdateSurveyForm.cancel(); // Cancel pending debounced calls
    },
    [debouncedUpdateSurveyForm]
  );

  // temporary FE handle survey preset code
  // handle preset data load
  useEffect(() => {
    if (isPresetInitialization && !isHandledPresetNameLoad) {
      const { title } = presetInfo;

      setSurveyName(title);
      updateSurveyForm({ surveyId, updatedFormFields: { name: title } });
      dispatch(setSurveyCreationPresetLoadKey({ isHandledPresetNameLoad: true }));
    }

    if (isPresetInitialization && !isHandledFrequencyLoad) {
      const { frequencyType, recurrenceRule } = presetInfo;

      if (frequencyType === FREQUENCY_TYPES.RECURRING) {
        setFrequency((prev) => ({
          type: FREQUENCY_TYPES.RECURRING,
          date: prev.date,
          openFor: prev.openFor,
          recurrenceRule: recurrenceRule(prev.date),
        }));
        // TODO: update in api here
        dispatch(setSurveyCreationPresetLoadKey({ isHandledFrequencyLoad: true }));
      }
    }
  }, []);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setSurveyName(newName);
    debouncedUpdateSurveyForm(newName);
  };

  const handleFrequencyChange = (updatedFrequencyFields: { date?: Date; openFor?: number }) => {
    setFrequency((prev) => {
      if (prev.type === FREQUENCY_TYPES.RECURRING) {
        return {
          ...prev,
          ...updatedFrequencyFields,
          recurrenceRule: generateDefaultRecurrenceRule(updatedFrequencyFields.date || prev.date),
        };
      }

      return {
        ...prev,
        ...updatedFrequencyFields,
      };
    });

    const startDate = updatedFrequencyFields.date || frequency.date;
    const endDate = addDays(startDate, updatedFrequencyFields.openFor || frequency.openFor);

    // Format dates in "YYYY-MM-DD" format (e.g., "2025-04-01")
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');

    updateSurveySettings({
      surveyId,
      updatedFormFields: { startDate: formattedStartDate, endDate: formattedEndDate },
    });
  };

  return (
    <SectionWrapper
      title="Survey name, timeline & participants"
      subTitle="Survey name, survey timeline, participants, recipients"
      restrictAccess={isReadOnly}
    >
      <LabelFieldWrapper label="Survey name" gridTemplateColumns="320px 1fr" gridGap={0}>
        <InputV2
          placeholder="Enter the name of this survey"
          isDashed
          onChange={handleNameChange}
          onBlur={() => debouncedUpdateSurveyForm.flush()}
          value={surveyName}
          width="300px"
          autoFocus={!isReadOnly && !surveyName}
        />
      </LabelFieldWrapper>

      <LabelFieldWrapper label="Timeline or frequency" gridTemplateColumns="320px 1fr" gridGap={0}>
        <Flex gap="8px">
          <Flex minW="300px" alignItems="center">
            {checkEnableSurveyPresetsAndRecurrence() ? (
              <Select
                value={frequency.type}
                onValueChange={(value: keyof typeof FREQUENCY_TYPES) => {
                  if (value === FREQUENCY_TYPES.ONE_TIME) {
                    setFrequency((prev) => ({ ...prev, type: value }));
                  } else if (value === FREQUENCY_TYPES.RECURRING) {
                    setFrequency((prev) => ({
                      ...prev,
                      type: value,
                      recurrenceRule: generateDefaultRecurrenceRule(prev.date),
                    }));
                  }
                }}
              >
                <SelectTrigger style={{ width: '300px' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQUENCY_TYPES).map(([key, value]) => (
                    <SelectItem key={key} value={value}>
                      <Flex gap={2} alignItems="center">
                        {FREQUENCY_LABELS[value]} {value === FREQUENCY_TYPES.RECURRING && <BetaTag />}
                      </Flex>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <TextBodyMedium>{FREQUENCY_LABELS[frequency.type]}</TextBodyMedium>
            )}
          </Flex>
          <Flex alignItems="center" gap="8px">
            <TextBodyMedium whiteSpace="noWrap">Starts on</TextBodyMedium>
            <DatePicker
              selectedDate={frequency.date}
              onChange={(date: Date) => handleFrequencyChange({ date })}
              minDate={dateConstraints.min}
              maxDate={dateConstraints.max}
            />
          </Flex>
          <Flex alignItems="center" gap="8px">
            <TextBodyMedium whiteSpace="noWrap">and open for</TextBodyMedium>
            <InputV2
              type="number"
              isDashed
              value={frequency.openFor}
              w="60px"
              min={1}
              max={365}
              onChange={(e) => {
                // min value is 1, max value is 365
                const value = Math.min(Math.max(Number(e.target.value) || 1, 1), 365);
                handleFrequencyChange({ openFor: value });
              }}
            />
            <TextBodyMedium>days</TextBodyMedium>
          </Flex>
        </Flex>
      </LabelFieldWrapper>

      {frequency.type === FREQUENCY_TYPES.RECURRING && (
        <LabelFieldWrapper label="Recurrence frequency" gridTemplateColumns="320px 1fr" gridGap={0}>
          <RecurrenceSelect
            date={frequency.date}
            recurrenceRule={frequency.recurrenceRule}
            onRecurrenceChange={(recurrenceRule) => {
              setFrequency((prev) => ({ ...prev, recurrenceRule }));
            }}
          />
        </LabelFieldWrapper>
      )}

      <LabelFieldWrapper label="Who is part of the survey" gridTemplateColumns="320px 1fr" gridGap={0}>
        <CategoryMultiSelectWrapper
          metaData={categoryMultiSelectData}
          setMetaData={setCategoryMultiSelectData}
          module={PARTICIPANTS_SELECTION_MODULES.ENGAGEMENT}
          customStyle={{ width: '300px' }}
        />
      </LabelFieldWrapper>
    </SectionWrapper>
  );
};
